/// <reference types="cordova-plugin-purchase" />

import { Capacitor } from '@capacitor/core';
import { supabase } from '../supabase';

// ─── Google Play billing for the four paid PDF books ─────────────────────────
//
// Sells `paid_books` as non-consumable Play products granting lifetime access.
// Completely separate from the rewarded-ad unlock in `ebookApi.ts`.
//
// THE ONE RULE: this file never grants anything.
//
// Everything here runs inside a WebView the user controls. A rooted device, a
// patched APK, or a Play-billing spoofing app can make every value below lie —
// `store.owned()` can return true, a fake `approved` event can be injected,
// prices can be rewritten. So nothing in this module writes an entitlement, and
// nothing in this module is consulted when a PDF is handed over. The only path
// to a row in `book_purchases` is the `verify-play-purchase` Edge Function,
// which asks Google itself whether money moved, and the only path to a PDF is
// `get-book-url`, which re-reads that row server-side. What this module
// produces is a *hint* for the UI: which button to draw. Being wrong about that
// costs a confusing screen, not a stolen book.
//
// Android only. The same React bundle is served as a website and is wrapped by
// a separate native SwiftUI app on iOS, neither of which has Play Billing. Every
// export below therefore returns an inert, well-typed value off Android rather
// than throwing — a broken Buy button on the website would be worse than no
// button, and callers should not need a platform check at each call site.

declare global {
  interface Window {
    /**
     * cordova-plugin-purchase has no ES module entry point — `package.json`
     * main is a self-executing script that Cordova loads via `<clobbers>`.
     * Importing the package would bundle a second, disconnected copy into the
     * CRA build, so the runtime object is only ever reached through `window`.
     *
     * It is also created inside a `setTimeout(..., 0)` that runs after
     * `cordova.js`, which is why a bare `CdvPurchase.store` at module scope
     * would be a ReferenceError on web and a race on device.
     */
    CdvPurchase?: typeof CdvPurchase;
  }
}

// ─── Product catalogue ───────────────────────────────────────────────────────
//
// These strings must match the in-app product IDs in Play Console character for
// character, and they must also exist as `paid_books.play_product_id` — the
// Edge Function refuses anything not in that table before it even calls Google.

export const PAID_BOOK_PRODUCT_IDS = [
  'book_a0_a1',
  'book_a2',
  'book_vocabulary',
  'book_b1',
] as const;

export type PaidBookProductId = (typeof PAID_BOOK_PRODUCT_IDS)[number];

// ─── Public result types ─────────────────────────────────────────────────────

/**
 * A product as Play describes it right now.
 *
 * `price` is null until Play answers — see `onProductsUpdated`.
 */
export interface BillingProduct {
  productId: string;
  /**
   * Play's own formatted price string, already localised to the currency and
   * numerals of the user's Play account country (e.g. "€14.99", "৳১,৪৯৯.০০").
   *
   * This is the ONLY price that may be shown. `paid_books.price_display` is an
   * admin-facing label in one currency; showing it to a user who will be
   * charged in another is a Play policy problem and a support ticket.
   */
  price: string | null;
  priceMicros: number | null;
  currency: string | null;
  /** Play Console title. Prefer the Bengali `title_ml` from the DB for display. */
  title: string | null;
  /** False while prices are still loading, or if Play has hidden the product. */
  canPurchase: boolean;
  /**
   * What Play believes about ownership. Advisory only — it is a client-side
   * value and the reader gates on `book_purchases` instead. Useful for drawing
   * "Owned" before the entitlement query returns.
   */
  ownedAccordingToPlay: boolean;
}

/** Mirrors the `FailReason` union in `supabase/functions/verify-play-purchase`. */
export type VerifyRejectionReason =
  | 'bad_request'
  | 'unauthorized'
  | 'unknown_product'
  | 'not_found'
  | 'purchase_pending'
  | 'purchase_cancelled'
  | 'product_mismatch'
  | 'test_purchase'
  | 'already_claimed'
  | 'purchase_revoked'
  | 'upstream_error'
  | 'server_error';

export type PurchaseFailureCode =
  /** Not Android, or the Play Billing plugin is not present in this build. */
  | 'unsupported_platform'
  /** Guest or signed-out: there is no JWT, so no purchase could ever be verified. */
  | 'not_signed_in'
  /** Play never returned this product — wrong ID, or not yet active in Console. */
  | 'unknown_product'
  /** Billing could not start: no Play Store, blocked by OEM policy, offline. */
  | 'store_unavailable'
  /** User dismissed the Play sheet. Not an error; show nothing. */
  | 'cancelled'
  /** Play refused the purchase for any other reason. */
  | 'play_error'
  /** Play said yes, our server said no. `reason` carries the server's verdict. */
  | 'rejected'
  /** Nothing came back in time. The purchase may still land — see below. */
  | 'timeout';

export interface PurchaseSuccess {
  ok: true;
  productId: string;
  /**
   * True when nothing was charged: the book was already owned and this call
   * only re-confirmed it. Lets the UI choose between "Thank you for your
   * purchase" and a silent unlock.
   *
   * It is not a perfect signal, and cannot be. The server answers `granted` for
   * a token it has already written, so a plain re-verify looks like a new sale
   * from here. Treat `false` as "probably new" rather than proof of a charge —
   * for anything that matters, read `book_purchases` via `fetchMyPurchases()`.
   */
  alreadyOwned: boolean;
}

export interface PurchaseFailure {
  ok: false;
  productId: string;
  code: PurchaseFailureCode;
  /** Present only when `code === 'rejected'`. */
  reason?: VerifyRejectionReason;
  /** English, for logs and as a last-resort UI fallback. Not user-facing copy. */
  message: string;
}

export type PurchaseResult = PurchaseSuccess | PurchaseFailure;

export interface RestoreResult {
  ok: boolean;
  /** Product IDs whose entitlement our server confirmed during this restore. */
  restored: string[];
  /** Owned in Play but refused by our server — refunds, replays, test purchases. */
  rejected: Array<{
    productId: string;
    reason?: VerifyRejectionReason;
    message: string;
  }>;
  /** Set when the restore itself could not run. */
  message?: string;
}

export interface BillingInitResult {
  ready: boolean;
  /** Why billing is unavailable, when it is. */
  reason?: 'unsupported_platform' | 'plugin_missing' | 'init_failed';
  /** Play adapter errors from `store.initialize()`, for logs only. */
  errors?: string[];
}

// ─── Platform gate ───────────────────────────────────────────────────────────

/**
 * Whether this build could ever sell a book. `Capacitor.isNativePlatform()` is
 * not enough — it is also true inside the vestigial `ios/App` shell, where
 * there is no Play Billing at all.
 *
 * Deliberately does NOT check for `window.CdvPurchase`: the plugin is clobbered
 * onto `window` a tick after `cordova.js` loads, so a screen mounting early
 * would see `false` and hide the paid section permanently. This answers "is
 * this the Android app"; `initialiseBilling()` answers "did billing come up".
 */
export function isBillingSupported(): boolean {
  return Capacitor.getPlatform() === 'android';
}

// ─── Tunables ────────────────────────────────────────────────────────────────

/**
 * How long to wait for `deviceready` before deciding Cordova is not coming.
 * On web this event never fires at all, and a hung init would hang every
 * `purchase()` call behind it, so the wait is bounded rather than open-ended.
 */
const DEVICE_READY_TIMEOUT_MS = 8_000;

/**
 * How long `purchase()` waits for the verification round trip after Play
 * reports the billing flow launched. `order()` resolves as soon as the sheet is
 * on screen, not when the user pays, so this budget has to cover a human
 * reading a payment dialog, entering a card, and our Edge Function calling
 * Google. Generous on purpose: timing out early would tell a user who has just
 * been charged that nothing happened.
 */
const PURCHASE_TIMEOUT_MS = 180_000;

/** Restore is machine-speed — Play answers, then one Edge Function call each. */
const RESTORE_TIMEOUT_MS = 30_000;

/**
 * How long a restore keeps waiting on a book Play did NOT report as owned.
 * Not zero: the local receipts are rebuilt from a bridge message that arrives
 * just before `restorePurchases()` resolves, and cutting a genuinely owned book
 * off on a scheduling accident would be worse than two idle seconds.
 */
const RESTORE_UNREPORTED_GRACE_MS = 2_000;

/**
 * The plugin re-fires `approved` for every owned non-consumable on every launch
 * (that is deliberate — it is how a restore happens without a button) and there
 * is no response cache on the validator-function path. Without this, opening
 * the app with four books owned is four Edge Function invocations every time.
 * Only successes are cached: a rejection must always be allowed to retry.
 */
const VERIFY_CACHE_TTL_MS = 60_000;

// ─── Module state ────────────────────────────────────────────────────────────

let initPromise: Promise<BillingInitResult> | null = null;
let initResult: BillingInitResult = { ready: false, reason: 'unsupported_platform' };

/**
 * Read synchronously by `store.applicationUsername`, which the plugin calls
 * per-transaction. Kept as a getter over a snapshot because the session can
 * change (login, logout, refresh) long after billing was initialised.
 */
let currentUserId: string | undefined;

type ServerAnswer =
  | { ok: true; alreadyOwned: boolean }
  | { ok: false; reason?: VerifyRejectionReason; message: string };

/**
 * The validator knows the server's verdict; the `verified` / `unverified`
 * handlers know the transaction reached its terminal state. Neither alone can
 * resolve `purchase()`, so the verdict is parked here between the two.
 */
const lastServerAnswer = new Map<string, ServerAnswer>();

/** purchaseToken → when it was last confirmed. See VERIFY_CACHE_TTL_MS. */
const verifiedTokenCache = new Map<string, { at: number; alreadyOwned: boolean }>();

type Waiter = (result: PurchaseResult) => void;
const waiters = new Map<string, Waiter[]>();

const productListeners = new Set<() => void>();

function settle(productId: string, result: PurchaseResult): void {
  const list = waiters.get(productId);
  if (!list) return;
  waiters.delete(productId);
  list.forEach((w) => {
    try {
      w(result);
    } catch (e) {
      console.warn('[billing] waiter threw:', e);
    }
  });
}

interface PendingVerification {
  promise: Promise<PurchaseResult>;
  /** Shorten the deadline of an already-registered waiter. */
  reschedule(timeoutMs: number): void;
}

/**
 * Registered BEFORE `order()` is called, never after. Play can report an
 * approved purchase almost instantly on a saved payment method, and a waiter
 * attached afterwards would miss the event and sit until it times out.
 */
function waitForVerification(productId: string, timeoutMs: number): PendingVerification {
  let timer: ReturnType<typeof setTimeout>;
  let done = false;
  let waiter: Waiter;

  const remove = () => {
    const list = waiters.get(productId);
    if (!list) return;
    const i = list.indexOf(waiter);
    if (i >= 0) list.splice(i, 1);
    if (list.length === 0) waiters.delete(productId);
  };

  let arm: (ms: number) => void = () => undefined;

  const promise = new Promise<PurchaseResult>((resolve) => {
    arm = (ms: number) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (done) return;
        done = true;
        remove();
        resolve({
          ok: false,
          productId,
          code: 'timeout',
          message: 'Verification did not complete in time',
        });
      }, ms);
    };

    waiter = (result) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      resolve(result);
    };

    arm(timeoutMs);

    const list = waiters.get(productId);
    if (list) list.push(waiter);
    else waiters.set(productId, [waiter]);
  });

  return {
    promise,
    reschedule: (ms: number) => {
      if (!done) arm(ms);
    },
  };
}

function notifyProductsUpdated(): void {
  productListeners.forEach((cb) => {
    try {
      cb();
    } catch (e) {
      console.warn('[billing] product listener threw:', e);
    }
  });
}

function productIdsOf(receipt: CdvPurchase.Receipt): string[] {
  const ids: string[] = [];
  receipt.transactions.forEach((t) => {
    t.products.forEach((p) => {
      if (p.id && ids.indexOf(p.id) < 0) ids.push(p.id);
    });
  });
  return ids;
}

// ─── Talking to our server ───────────────────────────────────────────────────

/**
 * The single call that decides whether a purchase is real.
 *
 * Note what is NOT sent: a user id. Identity is taken from the bearer JWT that
 * supabase-js attaches, and the function ignores any `user_id` in the body on
 * purpose — accepting one would let any caller grant a book to any account, or
 * launder one receipt across many.
 */
async function verifyWithServer(
  productId: string,
  purchaseToken: string,
): Promise<ServerAnswer> {
  // No session means no JWT, and the function answers 401 to the anon key by
  // design. Short-circuit rather than spending a round trip to be told so —
  // this fires on every launch for a signed-out user with an old purchase.
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData?.session) {
    return { ok: false, reason: 'unauthorized', message: 'Not signed in' };
  }

  const { data, error } = await supabase.functions.invoke('verify-play-purchase', {
    body: { productId, purchaseToken },
  });

  // The function returns HTTP 200 for every *decided* outcome, success or
  // refusal, and non-2xx only when it could not decide. So an `error` here is
  // "ask again later", and a 200 with ok:false is "no, and stop asking".
  if (error) {
    let serverReason: VerifyRejectionReason | undefined;
    try {
      const ctx = (error as { context?: Response }).context;
      if (ctx && typeof ctx.json === 'function') {
        const body: { reason?: VerifyRejectionReason } = await ctx.json();
        serverReason = body?.reason;
      }
    } catch {
      // Body already consumed or not JSON — the generic message still applies.
    }
    return {
      ok: false,
      reason: serverReason,
      message: error.message || 'Could not reach the verification server',
    };
  }

  const body = data as
    | { ok: true; status?: 'granted' | 'already_owned' }
    | { ok: false; reason?: VerifyRejectionReason }
    | null;

  if (!body?.ok) {
    return {
      ok: false,
      reason: body?.reason,
      message: body?.reason ?? 'Verification refused',
    };
  }

  return { ok: true, alreadyOwned: body.status === 'already_owned' };
}

// ─── The validator ───────────────────────────────────────────────────────────

/**
 * `store.validator` in function form. The plugin also accepts a URL, but its
 * own HTTP body is a different shape from `{ productId, purchaseToken }`, and
 * the URL path cannot attach a Supabase session JWT.
 *
 * Contract quirks that are easy to get wrong:
 *
 *  - It is invoked synchronously with no return value. `callback(...)` is the
 *    ONLY thing that advances the flow. Forget it, or throw synchronously (the
 *    plugin swallows the exception), and the transaction stalls forever —
 *    `verified` never fires, `finish()` is never reached, and the user's money
 *    is in limbo. Hence the blanket try/catch.
 *  - `data.collection` is not optional for us. Once `store.validator` is set,
 *    `store.owned()` reads verified receipts instead of local ones, so omitting
 *    the collection leaves a genuinely purchased book reading as unowned.
 *  - `collection[].isAcknowledged: true` with an exactly matching
 *    `transactionId` is what stops the plugin acknowledging the purchase a
 *    SECOND time. Our Edge Function already acknowledged it through the Play
 *    Developer API; a second acknowledge fails, `finish()` silently swallows
 *    that failure, the transaction never leaves APPROVED, and `approved` then
 *    re-fires forever, re-invoking the Edge Function on a loop. The
 *    `transactionId` must be echoed from `body.transaction.id`, never rebuilt —
 *    it is Play's `orderId` when there is one and the purchase token when there
 *    is not, and a near-miss silently reopens the loop.
 */
const validatePlayPurchase: CdvPurchase.Validator.Function = (body, callback) => {
  const cdv = window.CdvPurchase;

  void (async () => {
    try {
      if (!cdv) {
        return callback({ ok: false, message: 'Billing plugin unavailable' });
      }

      const tx = body.transaction;
      const productId = body.id;

      if (!tx || tx.type !== cdv.Platform.GOOGLE_PLAY || !tx.purchaseToken || !productId) {
        return callback({
          ok: false,
          code: cdv.ErrorCode.VERIFICATION_FAILED,
          message: 'Not a Google Play product purchase',
        });
      }

      const purchaseToken = tx.purchaseToken;
      const transactionId = tx.id;

      const cached = verifiedTokenCache.get(purchaseToken);
      const answer: ServerAnswer =
        cached && Date.now() - cached.at < VERIFY_CACHE_TTL_MS
          ? { ok: true, alreadyOwned: cached.alreadyOwned }
          : await verifyWithServer(productId, purchaseToken);

      lastServerAnswer.set(productId, answer);

      if (!answer.ok) {
        // Refusal is reported, never worked around. The transaction stays
        // APPROVED and unfinished on purpose: the plugin will re-offer it on
        // the next receipts refresh, so a transient server failure heals by
        // itself, and a genuine refusal (refund, replay, test purchase) simply
        // never unlocks anything.
        return callback({
          ok: false,
          code: cdv.ErrorCode.VERIFICATION_FAILED,
          message: answer.reason ?? answer.message,
        });
      }

      verifiedTokenCache.set(purchaseToken, {
        at: Date.now(),
        alreadyOwned: answer.alreadyOwned,
      });

      callback({
        ok: true,
        data: {
          // Keys the verified-receipt cache. Without it the receipt is dropped.
          id: productId,
          latest_receipt: true,
          transaction: {
            type: cdv.Platform.GOOGLE_PLAY,
            kind: 'androidpublisher#productPurchase',
            productId,
            purchaseToken,
            orderId: transactionId,
            purchaseState: 0, // 0 = Purchased. Our server already proved this.
            acknowledgementState: 1, // Acknowledged — by the Edge Function.
          },
          collection: [
            {
              id: productId,
              platform: cdv.Platform.GOOGLE_PLAY,
              transactionId,
              purchaseId: purchaseToken,
              purchaseDate: Date.now(),
              // Lifetime access: no expiryDate, and never expired.
              isExpired: false,
              // See the doc comment — this is the double-acknowledge guard.
              isAcknowledged: true,
            },
          ],
        },
      });
    } catch (e) {
      // A thrown validator is indistinguishable from a hung one, so every path
      // out of here has to end in a callback.
      callback({
        ok: false,
        code: cdv?.ErrorCode.VERIFICATION_FAILED,
        message: (e as Error)?.message ?? 'Verification failed',
      });
    }
  })();
};

// ─── Initialisation ──────────────────────────────────────────────────────────

/**
 * Cordova replaces `document.addEventListener` for `deviceready` so that late
 * subscribers still fire, which makes this safe to call at any point in the
 * app's life. The timeout only covers the case where `cordova.js` was never
 * loaded at all.
 */
function awaitDeviceReady(timeoutMs: number): Promise<void> {
  return new Promise<void>((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    const timer = setTimeout(finish, timeoutMs);
    document.addEventListener(
      'deviceready',
      () => {
        clearTimeout(timer);
        finish();
      },
      { once: true },
    );
  });
}

/**
 * The plugin builds `window.CdvPurchase` from a `setTimeout(..., 0)` scheduled
 * when `cordova.js` evaluates, so it can still be a tick or two behind
 * `deviceready`. Polling briefly is cheaper than the alternative, which is
 * every early caller getting a spurious "billing unavailable".
 */
async function waitForPlugin(): Promise<typeof CdvPurchase | null> {
  await awaitDeviceReady(DEVICE_READY_TIMEOUT_MS);
  for (let i = 0; i < 20; i++) {
    if (window.CdvPurchase) return window.CdvPurchase;
    await new Promise<void>((r) => setTimeout(r, 50));
  }
  return window.CdvPurchase ?? null;
}

/**
 * Registers the four products and connects to Play. Safe to call from anywhere,
 * as often as you like — the work happens once and every later caller awaits
 * the same promise. `store.initialize()` itself is once-only and warns on a
 * second call, so the shared promise is not just an optimisation.
 *
 * Call this early (app start, or when the books screen mounts). Prices arrive
 * asynchronously from Play afterwards; see `onProductsUpdated`.
 */
export function initialiseBilling(): Promise<BillingInitResult> {
  if (initPromise) return initPromise;

  if (Capacitor.getPlatform() !== 'android') {
    initResult = { ready: false, reason: 'unsupported_platform' };
    initPromise = Promise.resolve(initResult);
    return initPromise;
  }

  initPromise = (async (): Promise<BillingInitResult> => {
    const cdv = await waitForPlugin();
    if (!cdv) {
      console.warn('[billing] cordova-plugin-purchase not present');
      initResult = { ready: false, reason: 'plugin_missing' };
      return initResult;
    }

    try {
      const store = cdv.store;

      // Seed the user id before anything can order, and keep it current. Play
      // stamps the obfuscated form onto the purchase as
      // obfuscatedExternalAccountId, which is what lets a later Play server
      // notification (a refund, say) be tied back to an account. It grants
      // nothing on its own — the entitlement still comes from the JWT.
      const { data: sessionData } = await supabase.auth.getSession();
      currentUserId = sessionData?.session?.user?.id;
      supabase.auth.onAuthStateChange((_event, session) => {
        const nextUserId = session?.user?.id;
        if (nextUserId !== currentUserId) {
          // Both caches are keyed by purchase token / product, NOT by user, so
          // they must not survive an account switch. Otherwise: A verifies a
          // token, signs out inside the TTL, B signs in, the plugin re-fires
          // `approved` for that token, and the cache answers ok without asking
          // the server — handing B an "owned" book they never bought. The
          // reader still gates on get-book-url, so it is not free content, but
          // it is the one place the client can claim an entitlement it lacks.
          verifiedTokenCache.clear();
          lastServerAnswer.clear();
        }
        currentUserId = nextUserId;
      });

      store.verbosity = cdv.LogLevel.WARNING;
      store.applicationUsername = () => currentUserId;
      store.obfuscator = 'uuid';

      // MUST be assigned before initialize(). A receipt restored during startup
      // is validated the moment the adapter reports it, and a validator set
      // afterwards would miss that first pass.
      store.validator = validatePlayPurchase;

      // MUST be called before initialize(): the adapter snapshots the
      // registered-product list at init time and skips loading products *and
      // receipts* entirely when that list is empty.
      store.register(
        PAID_BOOK_PRODUCT_IDS.map((id) => ({
          id,
          type: cdv.ProductType.NON_CONSUMABLE,
          platform: cdv.Platform.GOOGLE_PLAY,
          // `group` is left unset deliberately — it only drives subscription
          // replacement, and setting it would make the books mutually exclusive.
        })),
      );

      store
        .when()
        // Prices land here, not at initialize(). The UI has to repaint.
        .productUpdated(() => notifyProductsUpdated())
        // Play says a purchase exists. That is a claim, not proof — hand it to
        // the validator, which asks our server, which asks Google.
        .approved((transaction) => {
          void transaction.verify();
        })
        // Reached only after our server returned ok. Finishing here rather than
        // in `approved` is what makes the money contingent on OUR verification
        // instead of on Play's word alone, and it is also the only overload of
        // finish() that can skip the plugin's duplicate acknowledge.
        .verified((receipt) => {
          const productId = receipt.id;
          void receipt.finish().catch((e: unknown) => {
            console.warn('[billing] finish failed', productId, e);
          });
          const answer = lastServerAnswer.get(productId);
          settle(productId, {
            ok: true,
            productId,
            alreadyOwned: answer?.ok === true ? answer.alreadyOwned : false,
          });
          notifyProductsUpdated();
        })
        // The only signal that our server refused. Deliberately does NOT
        // finish: an unfinished transaction is re-offered on the next refresh,
        // which is exactly the retry a transient failure needs.
        .unverified(({ receipt, payload }) => {
          console.warn('[billing] unverified', payload.code, payload.message);
          productIdsOf(receipt).forEach((productId) => {
            const answer = lastServerAnswer.get(productId);
            settle(productId, {
              ok: false,
              productId,
              code: 'rejected',
              reason: answer?.ok === false ? answer.reason : undefined,
              message:
                (answer?.ok === false ? answer.message : undefined) ??
                payload.message ??
                'Purchase could not be verified',
            });
          });
        })
        // Deferred payment methods (cash, carrier billing) sit here for hours
        // and may never complete. Google is explicit: grant nothing on PENDING.
        .pending((transaction) => {
          console.log('[billing] payment pending', transaction.transactionId);
        })
        .receiptsVerified(() => notifyProductsUpdated());

      store.error((err) => {
        console.warn('[billing]', err.code, err.message);
      });

      const errors = await store.initialize([cdv.Platform.GOOGLE_PLAY]);
      if (errors && errors.length) {
        console.warn('[billing] initialize reported errors', errors);
      }

      initResult = {
        ready: true,
        errors: errors?.map((e) => `${e.code}: ${e.message}`),
      };
      return initResult;
    } catch (e) {
      console.warn('[billing] init failed:', e);
      initResult = { ready: false, reason: 'init_failed' };
      return initResult;
    }
  })();

  return initPromise;
}

// ─── Products and prices ─────────────────────────────────────────────────────

/**
 * Play's view of the four products, in catalogue order.
 *
 * Returns entries with `price: null` before Play answers rather than an empty
 * array, so the store screen can render its cards immediately and fill the
 * price in. Returns `[]` off Android and when the plugin is absent, which
 * renders no paid section at all — that is the web and iOS behaviour.
 *
 * Synchronous by design — call it from render, and re-render from
 * `onProductsUpdated`.
 */
export function getProducts(): BillingProduct[] {
  const cdv = window.CdvPurchase;
  if (Capacitor.getPlatform() !== 'android' || !cdv) return [];

  return PAID_BOOK_PRODUCT_IDS.map((productId) => {
    const product = cdv.store.get(productId, cdv.Platform.GOOGLE_PLAY);
    const pricing = product?.pricing;
    return {
      productId,
      price: pricing?.price ?? null,
      priceMicros: pricing?.priceMicros ?? null,
      currency: pricing?.currency ?? null,
      title: product?.title ?? null,
      canPurchase: product?.canPurchase ?? false,
      ownedAccordingToPlay: cdv.store.owned({
        id: productId,
        platform: cdv.Platform.GOOGLE_PLAY,
      }),
    };
  });
}

/**
 * Subscribe to "something about the products changed" — a price arriving from
 * Play, or a receipt finishing verification. Returns an unsubscribe function.
 *
 * Prices are undefined at mount and appear seconds later over the network, so a
 * screen that reads `getProducts()` once will show four blank prices forever.
 */
export function onProductsUpdated(callback: () => void): () => void {
  productListeners.add(callback);
  return () => {
    productListeners.delete(callback);
  };
}

// ─── Purchase ────────────────────────────────────────────────────────────────

/**
 * Buys one book, end to end: Play payment sheet → our Edge Function → unlock.
 *
 * Resolves only once our server has ruled on the purchase. It never rejects;
 * every outcome including cancellation comes back as a typed `PurchaseResult`,
 * because a payment flow has too many ordinary non-success endings for
 * try/catch to be the right shape at the call site.
 *
 * On `{ ok: false }` nothing was unlocked and, for anything other than
 * `cancelled`, the transaction was deliberately left unfinished so the plugin
 * retries it on the next refresh. In particular a `timeout` is NOT a failed
 * purchase — the user may well have paid, and the entitlement will appear on
 * the next launch. Tell them to reopen the app, never that the payment failed.
 */
export async function purchase(productId: string): Promise<PurchaseResult> {
  if (Capacitor.getPlatform() !== 'android') {
    return {
      ok: false,
      productId,
      code: 'unsupported_platform',
      message: 'In-app purchases are only available in the Android app',
    };
  }

  const init = await initialiseBilling();
  const cdv = window.CdvPurchase;
  if (!init.ready || !cdv) {
    return {
      ok: false,
      productId,
      code: init.reason === 'plugin_missing' ? 'unsupported_platform' : 'store_unavailable',
      message: `Billing unavailable (${init.reason ?? 'unknown'})`,
    };
  }

  // Checked BEFORE the payment sheet, not after. A guest has no Supabase
  // session and therefore no JWT, so `verify-play-purchase` could never confirm
  // their purchase — they would be charged for a book that can never be
  // unlocked, and Play would auto-refund it three days later. Route to login.
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData?.session) {
    return {
      ok: false,
      productId,
      code: 'not_signed_in',
      message: 'Sign in before buying so the purchase can be linked to your account',
    };
  }

  // With a validator configured, `owned()` reads VERIFIED receipts — i.e. it is
  // only true for a purchase our own server has already confirmed this session.
  // So this is not a client-side grant, it is a short-circuit that avoids
  // sending someone to a Play sheet that would only answer ITEM_ALREADY_OWNED.
  if (cdv.store.owned({ id: productId, platform: cdv.Platform.GOOGLE_PLAY })) {
    return { ok: true, productId, alreadyOwned: true };
  }

  const product = cdv.store.get(productId, cdv.Platform.GOOGLE_PLAY);
  const offer = product?.getOffer();
  if (!product || !offer) {
    // Either the ID is not one of ours, or Play has not returned it — typically
    // a product that is not Active in Console, or a build signed with the wrong
    // key. Both look identical from here.
    return {
      ok: false,
      productId,
      code: 'unknown_product',
      message: 'This book is not available from the Play Store right now',
    };
  }

  // Attached before ordering — see waitForVerification.
  const verification = waitForVerification(productId, PURCHASE_TIMEOUT_MS);

  const err = await offer.order();
  if (err) {
    if (err.code === cdv.ErrorCode.PAYMENT_CANCELLED) {
      return { ok: false, productId, code: 'cancelled', message: 'Purchase cancelled' };
    }

    // ITEM_ALREADY_OWNED means Play holds a purchase our server has never
    // confirmed — a reinstall, an account switch, or a verification that failed
    // earlier. It is a restore, not an error. But Play reports it as a plain
    // failure and does NOT refresh the receipt list on its own, so the refresh
    // has to be asked for explicitly. The deadline drops to machine speed at
    // the same time: the long budget exists for a human reading a payment
    // sheet, and there is no human in this loop.
    if (/ITEM_ALREADY_OWNED/i.test(err.message || '')) {
      verification.reschedule(RESTORE_TIMEOUT_MS);
      void cdv.store.restorePurchases();
      const restored = await verification.promise;
      // Play refused to charge, so whatever the server calls it, this was not
      // a new sale.
      return restored.ok ? { ...restored, alreadyOwned: true } : restored;
    }

    const blocked =
      err.code === cdv.ErrorCode.STORE_BLOCKED ||
      /BILLING_UNAVAILABLE|SERVICE_UNAVAILABLE|SERVICE_DISCONNECTED|SERVICE_TIMEOUT/i.test(
        err.message || '',
      );
    return {
      ok: false,
      productId,
      code: blocked ? 'store_unavailable' : 'play_error',
      message: err.message || 'Google Play could not complete the purchase',
    };
  }

  return verification.promise;
}

// ─── Restore ─────────────────────────────────────────────────────────────────

/**
 * Re-syncs everything the signed-in Google account owns.
 *
 * Restore needs no special server path: `queryPurchasesAsync` re-delivers the
 * owned purchases, they arrive as ordinary `approved` transactions, and each
 * one goes through the same validator as a fresh sale. `verify-play-purchase`
 * is idempotent — it recognises a token it has already written and answers
 * `already_owned` — so re-running it after a reinstall costs one round trip and
 * rebuilds the entitlement rows on the new install.
 *
 * The same sweep also runs automatically at startup and every 24 hours, so this
 * exists mostly as a visible affordance for a user who expects a button.
 */
export async function restorePurchases(): Promise<RestoreResult> {
  if (Capacitor.getPlatform() !== 'android') {
    return { ok: false, restored: [], rejected: [], message: 'Android only' };
  }

  const init = await initialiseBilling();
  const cdv = window.CdvPurchase;
  if (!init.ready || !cdv) {
    return {
      ok: false,
      restored: [],
      rejected: [],
      message: `Billing unavailable (${init.reason ?? 'unknown'})`,
    };
  }

  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData?.session) {
    return {
      ok: false,
      restored: [],
      rejected: [],
      message: 'Sign in to restore your purchases',
    };
  }

  // Cleared so a restore always re-asks the server. The user pressed a button
  // because something looked wrong; serving them a cached "yes" from sixty
  // seconds ago is the one case where the dedupe is actively unhelpful.
  verifiedTokenCache.clear();

  // Waiters for all four go up first: `restorePurchases()` resolves when Play
  // has answered, but the verifications it triggers land after that.
  const pending = PAID_BOOK_PRODUCT_IDS.map((id) =>
    waitForVerification(id, RESTORE_TIMEOUT_MS),
  );

  const err = await cdv.store.restorePurchases();
  if (err) {
    console.warn('[billing] restore failed', err.code, err.message);
  }

  // Only the books Play just reported will ever produce a verification, so the
  // rest get their deadline cut short. Without this, a user who owns nothing
  // watches the Restore button spin for the full timeout before being told
  // there was nothing to restore.
  const reportedByPlay = new Set<string>();
  cdv.store.localTransactions.forEach((t) => {
    t.products.forEach((p) => reportedByPlay.add(p.id));
  });
  pending.forEach((p, i) => {
    if (!reportedByPlay.has(PAID_BOOK_PRODUCT_IDS[i])) {
      p.reschedule(RESTORE_UNREPORTED_GRACE_MS);
    }
  });

  const results = await Promise.all(pending.map((p) => p.promise));

  const restored: string[] = [];
  const rejected: RestoreResult['rejected'] = [];
  results.forEach((r) => {
    if (r.ok) restored.push(r.productId);
    else if (r.code === 'rejected') {
      rejected.push({ productId: r.productId, reason: r.reason, message: r.message });
    }
    // A `timeout` here just means the user does not own that book — three of
    // the four normally time out. It is not reported as a failure.
  });

  notifyProductsUpdated();

  return {
    ok: !err,
    restored,
    rejected,
    message: err ? err.message : undefined,
  };
}
